/**
 * QuotasCard — Settings → Sales (Phase 2 / 2.3b, F-4 / U-3): per-employee
 * sales targets for ONE selected period. The stage ladder's sibling: the same
 * tab, the same owner/admin-write / any-member-read posture (P2-10), its own
 * useAsync and its own error state — the Sales tab carries no page-level
 * loading because each card owns its own.
 *
 * Rows = the directory's active employees × the period (quotas.ts joins the
 * period's existing rows in, and keeps any row whose employee the roster
 * lacks). Each row is ONE write with ONE verb: a number is a PUT, a blank on
 * a row that has a target is a DELETE, "0" is a real target of zero (R3's
 * absent-vs-zero, on the write side). No "save all" — the server has no such
 * body, and each row is its own decision with its own actor.
 *
 * U-8: a 400 from the server is an INSTRUCTION (which date to send, which
 * verb to use) and is surfaced verbatim through apiErrorMessage with a
 * literal fallback — never replaced with "could not save". The one
 * status-specific line is the DELETE 404 (the server refuses to call
 * clearing nothing a success): its wire message is a bare codeword, so this
 * card says what the 404 means on THIS route.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { ApiError, apiErrorMessage } from '../../../../api/client'
import { LoadingState, ErrorState } from '../../DashboardShared'
import { useAsync, fetchDirectory, fetchSalesQuotas, upsertSalesQuota, deleteSalesQuota, fmtDate } from '../../api'
import type { ApiEmployeeDirectoryRow } from '../../types'
import type { SalesQuota } from '../../../../api/types'
import { PeriodPicker } from '../sales/PeriodPicker'
import { type SalesPeriod, currentPeriod, periodLabel } from '../sales/salesPeriod'
import { draftFromRoster, poundsText, quotaInputValid, quotaWrite, type QuotaDraftRow } from './quotas'
import { NO_TARGET_LABEL } from '../sales/forecastView'

const inputCls = 'w-32 px-3 py-2 border border-edge-control rounded-control text-13 text-ink-primary text-right transition-all'

export function QuotasCard() {
  const [period, setPeriod] = useState<SalesPeriod>(() => currentPeriod('quarter'))
  const roster = useAsync(() => fetchDirectory({ status: 'active' }), [])
  const quotas = useAsync(() => fetchSalesQuotas({ periodKind: period.kind, periodStart: period.start }), [period.kind, period.start])

  const loading = roster.loading || quotas.loading
  const error = roster.error ?? quotas.error

  return (
    <div className="k-card">
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-13 font-semibold text-ink-primary">Sales targets</div>
            <div className="text-12 text-ink-muted mt-1">
              What each employee is expected to win in the period, in GBP. Attainment on the Forecast tab is won value against this number. Leave a target blank for no target; 0 is a real target of zero.
            </div>
          </div>
          <PeriodPicker period={period} onChange={setPeriod} />
        </div>
        {loading && <LoadingState rows={3} />}
        {!loading && error && <ErrorState message={error} />}
        {!loading && !error && (
          // Keyed on the period so a period change re-drafts from the new
          // read (no state-in-effect); a save updates rows in place.
          <QuotasTable key={`${period.kind}:${period.start}`} period={period}
            roster={roster.data!} quotas={quotas.data!.items} />
        )}
      </div>
    </div>
  )
}

/** Exported for the Forecast tab's "Set targets" dialog (F5, register row 73):
 *  the SAME table and the same one-write-per-row verbs, never a fork. `onSaved`
 *  fires after each successful write so the caller can refetch. */
export function QuotasTable({ period, roster, quotas, onSaved }: { period: SalesPeriod; roster: ApiEmployeeDirectoryRow[]; quotas: SalesQuota[]; onSaved?: () => void }) {
  const [rows, setRows] = useState<QuotaDraftRow[]>(() => draftFromRoster(roster, quotas))
  const [busy, setBusy] = useState<string | null>(null) // employeeId mid-write

  const setValue = (employeeId: string, value: string) =>
    setRows(rs => rs.map(r => (r.employeeId === employeeId ? { ...r, value } : r)))
  const settle = (employeeId: string, stored: SalesQuota | null) =>
    setRows(rs => rs.map(r => (r.employeeId === employeeId
      ? { ...r, stored, value: stored ? poundsText(stored.targetPence) : '' }
      : r)))

  async function save(row: QuotaDraftRow) {
    const write = quotaWrite(row, period)
    if (!write || busy) return
    setBusy(row.employeeId)
    try {
      if (write.kind === 'upsert') {
        const saved = await upsertSalesQuota(write.body)
        toast.success(`Target saved for ${periodLabel(period)}`)
        settle(row.employeeId, saved)
        onSaved?.()
      } else {
        await deleteSalesQuota(write.body)
        toast.success(`Target cleared for ${periodLabel(period)}`)
        settle(row.employeeId, null)
        onSaved?.()
      }
    } catch (e) {
      if (write.kind === 'clear' && e instanceof ApiError && e.status === 404) {
        // The server's deliberate 404: there was no target to clear (someone
        // else already cleared it). Its wire message is the bare codeword,
        // so the sentence is this card's; the row re-reads as empty.
        toast.error('No target was set for this period — nothing to clear')
        settle(row.employeeId, null)
        onSaved?.()
      } else {
        toast.error(apiErrorMessage(e, write.kind === 'upsert' ? 'Failed to save target' : 'Failed to clear target'))
      }
    } finally {
      setBusy(null)
    }
  }

  if (rows.length === 0) {
    return (
      <div className="text-12 text-ink-muted">
        No active employees to set a target for — targets are set per employee, so add people in HR first.
      </div>
    )
  }

  return (
    <table className="sales-table w-full" aria-label={`Sales targets for ${periodLabel(period)}`}>
      <thead>
        <tr>
          <th className="text-left">Employee</th>
          <th className="text-right">Target</th>
          <th className="text-left">Status</th>
          <th className="text-right"><span className="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <QuotaRow key={r.employeeId} row={r} busy={busy === r.employeeId} anyBusy={busy !== null}
            write={quotaWrite(r, period)}
            onChange={v => setValue(r.employeeId, v)} onSave={() => void save(r)} />
        ))}
      </tbody>
    </table>
  )
}

function QuotaRow({ row, write, busy, anyBusy, onChange, onSave }: {
  row: QuotaDraftRow
  write: ReturnType<typeof quotaWrite>
  busy: boolean
  anyBusy: boolean
  onChange: (v: string) => void
  onSave: () => void
}) {
  const invalid = !quotaInputValid(row.value)
  return (
    <tr>
      <td className="text-13 text-ink-primary font-medium">{row.employeeName}</td>
      <td className="text-right">
        <span className="inline-flex items-center gap-1 justify-end">
          <span className="text-12 text-ink-muted">£</span>
          <input
            type="text" inputMode="decimal"
            value={row.value}
            placeholder="No target"
            onChange={e => onChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSave() } }}
            aria-label={`${row.employeeName} target`}
            aria-invalid={invalid || undefined}
            className={inputCls + (invalid ? ' border-danger' : '')}
          />
        </span>
      </td>
      <td className="text-12 text-ink-muted">
        {/* The stored state, not the draft: what the server holds right now.
            A £0 target is "Set" like any other — it is a real target. */}
        {row.stored
          ? `Set · updated ${fmtDate(row.stored.updatedAt)}`
          : NO_TARGET_LABEL}
      </td>
      <td className="text-right">
        {write?.kind === 'upsert' && (
          <button type="button" className="k-btn" disabled={anyBusy} onClick={onSave}>
            {busy ? 'Saving…' : 'Save target'}
          </button>
        )}
        {write?.kind === 'clear' && (
          <button type="button" className="k-btn-secondary" disabled={anyBusy} onClick={onSave}>
            {busy ? 'Clearing…' : 'Clear target'}
          </button>
        )}
      </td>
    </tr>
  )
}
